import { fetchServer } from "@features/utils";
import { useMutation, useQuery, useQueryClient } from "react-query";

const POPULAR_ITEM_KEY = "popular-item-modal";

type PopularItem = {
  url: string;
  type:
    | "lora_style"
    | "lora_model"
    | "image_image"
    | "image_hairstyle"
    | "image_outfit";
  popularity?: number; // Used to know if it's public or not yet
  name?: string;
  description?: string;
} | null;

type PopularItemState = {
  isOpen: boolean;
  currentItem: PopularItem;
};

export const useManagePopularItem = () => {
  const queryClient = useQueryClient();

  // Get modal state from the query cache
  const { data } = useQuery<PopularItemState>(
    [POPULAR_ITEM_KEY],
    () => ({
      isOpen: false,
      currentItem: null,
    }),
    { enabled: false }
  );

  const openModal = (item: PopularItem) => {
    queryClient.setQueryData([POPULAR_ITEM_KEY], {
      isOpen: true,
      currentItem: item,
    });
  };

  const closeModal = () => {
    queryClient.setQueryData([POPULAR_ITEM_KEY], {
      isOpen: false,
      currentItem: null,
    });
  };

  // Add or edit a popular item
  const { mutate: addPopular, isLoading: isAddingPopular } = useMutation(
    async ({
      item,
      name,
      description,
    }: {
      item: PopularItem;
      name: string;
      description: string;
    }) => {
      if (!item) return;
      return await fetchServer(`/api/popular/${item.type}`, {
        url: item.url,
        name,
        description,
      });
    },
    {
      onSuccess: () => {
        closeModal();
        // Invalidate related queries to refresh data
        queryClient.invalidateQueries(["popular-items"]);
      },
    }
  );

  // Function alias for editing (same operation as adding)
  const editPopular = addPopular;

  // Remove a popular item
  const { mutate: removePopular, isLoading: isRemovingPopular } = useMutation(
    async (item: PopularItem) => {
      if (!item) return;
      return await fetchServer(`/api/popular/${item.type}/remove`, {
        url: item.url,
      });
    },
    {
      onSuccess: () => {
        closeModal();
        // Invalidate related queries to refresh data
        queryClient.invalidateQueries(["popular-items"]);
      },
    }
  );

  // Remove from history
  const { mutate: removeFromHistory } = useMutation(
    async (item: PopularItem) => {
      if (!item) return;
      return await fetchServer(`/api/popular/${item.type}/unuse`, {
        url: item.url,
        delete: true,
      });
    },
    {
      onSuccess: () => {
        // Invalidate related queries to refresh data
        queryClient.invalidateQueries(["popular-items"]);
      },
    }
  );

  return {
    openModal,
    closeModal,
    addPopular,
    editPopular,
    removePopular,
    removeFromHistory,
    isAddingPopular,
    isRemovingPopular,
    isOpen: data?.isOpen || false,
    currentItem: data?.currentItem || null,
  };
};
